// Supplier Notifications dashboard section: KPI row + over-time chart + time-to-expedition
// horizontal distribution + sortable top-partners table.
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ApiContext } from '@api/api';
import { NotificationPartnerRowDTO, PartnerRolesAvailabilityDTO, SupplierNotificationStatisticsDTO } from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { formatNumber } from '@utils/utils.tsx';
import { useDashboardPref } from '../dashboardPrefs.tsx';
import { useDashboardDuration } from '../dashboardFormat.ts';
import { formatPrice } from '@utils/priceUtils';
import SvgMegaphone from '@icons/megaphone.svg?react';
import DashboardSelect from '../components/DashboardSelect.tsx';
import DashboardSection from '../components/DashboardSection.tsx';
import DashboardKpiCard from '../components/DashboardKpiCard.tsx';
import DashboardTable, { DashboardColumn } from '../components/DashboardTable.tsx';
import DashboardCard from '../components/DashboardCard.tsx';
import DashboardChartTitle from '../components/DashboardChartTitle.tsx';
import RingilChart from '../charts/RingilChart.tsx';
import { axisTooltipFormatter, barValueLabel, chartColors } from '../charts/chartTheme.ts';
import { isChartDataEmpty, timeSeriesZoom } from '../charts/chartOptions.ts';
import { NotificationPartnerSortBy, PartnerRole, SortDir, StatsRange } from '../dashboardTypes.ts';

interface SupplierNotificationsSectionProps {
  availability: PartnerRolesAvailabilityDTO;
}

const SupplierNotificationsSection: React.FC<SupplierNotificationsSectionProps> = ({ availability }) => {
  const { tr } = useTranslationLgs();
  const { getDashboardSectionSupplierNotifications, getDashboardSectionSupplierNotificationsTopPartners } = useContext(ApiContext);
  const formatDuration = useDashboardDuration();

  const bothRoles = availability.myCompany && availability.asPartner;
  const [storedRole, setRole] = useDashboardPref<PartnerRole>('supplierNotifications.role', availability.myCompany ? PartnerRole.MY_COMPANY : PartnerRole.PARTNER);
  // The stored role may no longer be available (permissions changed, different account on the same
  // browser) — clamp it to an available one, otherwise the section queries an empty variant.
  const role = bothRoles ? storedRole : availability.myCompany ? PartnerRole.MY_COMPANY : PartnerRole.PARTNER;
  // Shared across all sections so the 30 days / 6 months switch ties them together.
  const [range, setRange] = useDashboardPref<StatsRange>('dashboard.range', StatsRange.D30);
  const [sortBy, setSortBy] = useDashboardPref<NotificationPartnerSortBy>('supplierNotifications.sortBy', NotificationPartnerSortBy.NOTIFICATIONS);
  const [sortDir, setSortDir] = useDashboardPref<SortDir>('supplierNotifications.sortDir', SortDir.DESC);

  const [stats, setStats] = useState<SupplierNotificationStatisticsDTO>();
  const [partners, setPartners] = useState<NotificationPartnerRowDTO[]>([]);

  useEffect(() => {
    getDashboardSectionSupplierNotifications(role, range, data => setStats(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, range]);

  useEffect(() => {
    getDashboardSectionSupplierNotificationsTopPartners(role, range, sortBy, sortDir, data => setPartners(data ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, range, sortBy, sortDir]);

  const onSort = (key: NotificationPartnerSortBy) => {
    if (key === sortBy) setSortDir(sortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC);
    else {
      setSortBy(key);
      setSortDir(SortDir.DESC);
    }
  };

  const currency = stats?.currency ?? '';
  // "Time from SN to expedition" and the top-partners table are customer-side views (the consignee
  // who turns supplier notifications into expeditions), so they're hidden in the supplier (partner) role.
  const isCustomer = role === PartnerRole.MY_COMPANY;
  const comparison = range === StatsRange.D30 ? tr('dashboard.vsPrior30Days', 'vs. prior 30 days') : tr('dashboard.vsPrior6Months', 'vs. prior 6 months');
  const countLabel = tr('dashboard.supplierNotifications.count', 'Notifications');

  const notifUnit = tr('dashboard.supplierNotifications.countUnit', 'notif.');

  // Over-time: count bars with a legend (matching the other sections' labels/spacing).
  const overTimeOption = useMemo(() => {
    const ot = stats?.overTime ?? [];
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: axisTooltipFormatter(v => formatNumber(v, 0)),
      },
      legend: { top: 0, right: 0, data: [countLabel] },
      grid: { left: 0, right: 8, top: 40, bottom: 56, containLabel: true },
      ...timeSeriesZoom,
      xAxis: { type: 'category', data: ot.map(b => b.bucket) },
      yAxis: { type: 'value' },
      series: [
        {
          name: countLabel,
          type: 'bar',
          color: chartColors.success,
          data: ot.map(b => b.count),
          barMaxWidth: 40,
          label: barValueLabel(v => formatNumber(v, 0)),
        },
      ],
    };
  }, [stats, countLabel]);

  // Time-to-expedition: horizontal bars with a gray track and the count on the right.
  const distributionOption = useMemo(() => {
    const dist = stats?.timeToExpeditionDistribution ?? [];
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: axisTooltipFormatter(v => formatNumber(v, 0)),
      },
      grid: { left: 0, right: 56, top: 16, bottom: 16, containLabel: true },
      xAxis: { type: 'value', show: false },
      yAxis: { type: 'category', inverse: true, data: dist.map(b => b.label), axisTick: { show: false }, axisLine: { show: false } },
      series: [
        {
          name: countLabel,
          type: 'bar',
          color: chartColors.warning,
          data: dist.map(b => b.count),
          barMaxWidth: 22,
          showBackground: true,
          backgroundStyle: { color: chartColors.quaternary, borderRadius: 4 },
          itemStyle: { borderRadius: 4 },
          label: barValueLabel(v => `${formatNumber(v, 0)} ${notifUnit}`, 'right'),
        },
      ],
    };
  }, [stats, countLabel, notifUnit]);

  const overTimeEmpty = isChartDataEmpty((stats?.overTime ?? []).map(b => b.count));
  const distributionEmpty = isChartDataEmpty((stats?.timeToExpeditionDistribution ?? []).map(b => b.count));

  const columns: DashboardColumn<NotificationPartnerRowDTO, NotificationPartnerSortBy>[] = [
    { key: 'name', label: tr('dashboard.supplierNotifications.supplier', 'Supplier'), sortKey: NotificationPartnerSortBy.NAME, render: r => r.partnerName ?? '—' },
    { key: 'notifications', label: countLabel, sortKey: NotificationPartnerSortBy.NOTIFICATIONS, align: 'right', render: r => formatNumber(r.notifications, 0) },
    { key: 'relatedPrice', label: tr('dashboard.supplierNotifications.relatedPrice', 'Total related price'), sortKey: NotificationPartnerSortBy.TOTAL_RELATED_PRICE, align: 'right', render: r => formatPrice(r.totalRelatedPrice, currency, 0) },
  ];

  const controls = (
    <>
      {bothRoles && (
        <DashboardSelect
          value={role}
          onChange={setRole}
          options={[
            { code: PartnerRole.MY_COMPANY, label: tr('dashboard.role.myCompany', 'My company') },
            { code: PartnerRole.PARTNER, label: tr('dashboard.role.partner', 'Partners') },
          ]}
        />
      )}
      <DashboardSelect
        value={range}
        onChange={setRange}
        options={[
          { code: StatsRange.D30, label: tr('dashboard.range.d30', '30 days') },
          { code: StatsRange.M6, label: tr('dashboard.range.m6', '6 months') },
        ]}
      />
    </>
  );

  return (
    <DashboardSection title={tr('dashboard.supplierNotifications.title', 'Supplier notifications')} icon={<SvgMegaphone />} controls={controls}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <DashboardKpiCard label={tr('dashboard.supplierNotifications.total', 'Total notifications')} value={formatNumber(stats?.totalNotifications?.current ?? 0, 0)} changePct={stats?.totalNotifications?.changePct} comparisonLabel={comparison} />
        {stats?.timeToExpeditionMinutes && (
          <DashboardKpiCard label={tr('dashboard.supplierNotifications.timeToExpedition', 'Time to expedition')} value={formatDuration(stats.timeToExpeditionMinutes.current)} changePct={stats.timeToExpeditionMinutes.changePct} positiveIsGood={false} comparisonLabel={comparison} />
        )}
        {stats?.relatedPrice && <DashboardKpiCard label={tr('dashboard.supplierNotifications.relatedPrice', 'Related price')} value={formatPrice(stats.relatedPrice.current ?? 0, currency, 0)} changePct={stats.relatedPrice.changePct} comparisonLabel={comparison} />}
      </div>

      <div className={`grid grid-cols-1 gap-4 ${isCustomer ? 'lg:grid-cols-2' : ''}`}>
        <DashboardCard>
          <DashboardChartTitle title={tr('dashboard.supplierNotifications.overTime', 'Notifications over time')} />
          <RingilChart option={overTimeOption} height={300} notMerge isEmpty={overTimeEmpty} />
        </DashboardCard>
        {isCustomer && (
          <DashboardCard>
            <DashboardChartTitle title={tr('dashboard.supplierNotifications.timeDistribution', 'Time from SN to expedition')} />
            <RingilChart option={distributionOption} height={300} notMerge isEmpty={distributionEmpty} />
          </DashboardCard>
        )}
      </div>

      {isCustomer && (
        <DashboardCard>
          <DashboardChartTitle title={tr('dashboard.supplierNotifications.topPartners', 'Top partners')} />
          <DashboardTable columns={columns} rows={partners} rowKey={(r, i) => r.partnerId ?? `${i}`} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
        </DashboardCard>
      )}
    </DashboardSection>
  );
};

export default SupplierNotificationsSection;